import { getActiveAbsenceReasonsRequest } from 'api/requests/absence-reason-requests';
import { UPDropdownItem } from 'components/dropdown/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ABSENCE_REASON__DEACTIVATED_BADGE } from 'translations/constants';
import { IActiveAbsenceReason, INoShowReasonDetail } from 'types/absence-reason/IAbsenceReason';
import { getReasonName } from 'utils/absence-reason';

export interface UseNoShowReasonOptionsResult {
  items: UPDropdownItem[];
  reload: () => Promise<void>;
}

/**
 * Options of the no-show reason selector: the agency's active absence reasons (value = reason id as a string).
 * `current` is the reason already on the service; when it has been deactivated it is not in the active list,
 * so it is added for this row only, marked as deactivated, and can still be confirmed.
 */
export const useNoShowReasonOptions = (current?: INoShowReasonDetail | null): UseNoShowReasonOptionsResult => {
  const { t, i18n } = useTranslation();
  const [reasons, setReasons] = useState<IActiveAbsenceReason[]>([]);
  const latestRequest = useRef(0);

  const reload = useCallback(async () => {
    latestRequest.current += 1;
    const requestId = latestRequest.current;
    try {
      const active = await getActiveAbsenceReasonsRequest();
      if (requestId === latestRequest.current) setReasons(active);
    } catch {
      // Keep the options already shown; the selector stays usable and the next reload can recover.
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const items = useMemo(() => {
    const options: UPDropdownItem[] = reasons.map(reason => ({
      value: String(reason.id),
      label: getReasonName(reason, i18n.language),
      testId: `noshow-reason-option-${reason.id}`,
    }));
    if (current && !current.isActive && !reasons.some(reason => reason.id === current.id)) {
      options.push({
        value: String(current.id),
        label: `${getReasonName(current, i18n.language)} (${t(ABSENCE_REASON__DEACTIVATED_BADGE)})`,
        testId: `noshow-reason-deactivated-${current.id}`,
      });
    }
    return options;
  }, [reasons, current, i18n.language, t]);

  return { items, reload };
};
